import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { BarChart } from "@mui/x-charts/BarChart";
import { PAY_PLAN_APP, PAY_PLAN_APP_LABELS } from "../../services/PayPlanUtil";
import Logger from "../../services/Logger";
import AccountService from "./AccountService";

const APP_ORDER = [
    { key: PAY_PLAN_APP.WEB, label: PAY_PLAN_APP_LABELS[PAY_PLAN_APP.WEB] || "Web" },
    { key: PAY_PLAN_APP.JIGSAW_MUSE, label: PAY_PLAN_APP_LABELS[PAY_PLAN_APP.JIGSAW_MUSE] || "Jigsaw" },
    { key: PAY_PLAN_APP.WORD_WHEEL_QUEST, label: PAY_PLAN_APP_LABELS[PAY_PLAN_APP.WORD_WHEEL_QUEST] || "Word Wheel" },
    { key: PAY_PLAN_APP.SLIDING_PUZZLE_QUEST, label: "Sliding Puzzle" },
];

const APP_LABEL_BY_KEY = Object.fromEntries(APP_ORDER.map((app) => [app.key, app.label]));

const formatNumber = (value) =>
    Number.isFinite(Number(value)) ? Number(value).toLocaleString() : "0";

function testAccountParam(filter) {
    if (filter === "exclude") return false;
    if (filter === "include") return true;
    return undefined;
}

function platformLabel(platform) {
    const key = String(platform || "").toLowerCase();
    if (key === "ios" || key === "apple") return "iOS";
    if (key === "android") return "Android";
    if (key === "web") return "Web";
    return platform || "—";
}

function formatWhen(epochMs) {
    if (epochMs == null) return "—";
    const date = new Date(epochMs);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString();
}

function displayName(row) {
    const name = typeof row?.name === "string" ? row.name.trim() : "";
    const email = typeof row?.email === "string" ? row.email.trim() : "";
    if (!name || (email && name.toLowerCase() === email.toLowerCase())) {
        return "Anonymous";
    }
    return name;
}

export default function AccountNotificationStats({
    keyword = "",
    testAccountFilter = "exclude",
    excludeStaffUser = false,
    excludeAppleStaff = false,
}) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [awsUsers, setAwsUsers] = useState([]);
    const [awsError, setAwsError] = useState(null);
    const [appFilter, setAppFilter] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            setAwsError(null);
            const params = {
                keyword,
                testAccount: testAccountParam(testAccountFilter),
                staff: excludeStaffUser ? false : undefined,
                excludeApplePrivateRelay: excludeAppleStaff,
            };
            try {
                const [countsResult, awsResult] = await Promise.allSettled([
                    AccountService.notificationCountsByPlatform(params),
                    AccountService.notificationEnabledOnAws(params),
                ]);
                if (cancelled) return;
                if (countsResult.status === "fulfilled") {
                    setStats(countsResult.value ?? null);
                } else {
                    Logger.error("Failed to load notification app stats:", countsResult.reason);
                    setError("Failed to load notification counts.");
                    setStats(null);
                }
                if (awsResult.status === "fulfilled") {
                    const rows = Array.isArray(awsResult.value?.users) ? awsResult.value.users : [];
                    setAwsUsers(rows);
                } else {
                    Logger.error("Failed to load AWS SNS notification users:", awsResult.reason);
                    setAwsUsers([]);
                    setAwsError("Failed to load who is registered on AWS SNS.");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [keyword, testAccountFilter, excludeStaffUser, excludeAppleStaff]);

    const apps = useMemo(() => {
        const byCode = new Map(
            (Array.isArray(stats?.apps) ? stats.apps : []).map((row) => [row.appCode, row]),
        );
        return APP_ORDER.map((app) => {
            const row = byCode.get(app.key) || {};
            return {
                key: app.key,
                label: app.label,
                on: Number(row.on) || 0,
                off: Number(row.off) || 0,
                none: Number(row.none) || 0,
            };
        });
    }, [stats]);

    const filteredAwsUsers = useMemo(() => {
        if (!appFilter) return awsUsers;
        return awsUsers.filter((row) => row.appCode === appFilter);
    }, [awsUsers, appFilter]);

    useEffect(() => {
        setPage(0);
    }, [appFilter, awsUsers]);

    const awsOnCount = filteredAwsUsers.filter((row) => row.optedIn !== false).length;
    const pageCount = Math.max(1, Math.ceil(filteredAwsUsers.length / rowsPerPage));
    const safePage = Math.min(page, pageCount - 1);
    const pagedAwsUsers = filteredAwsUsers.slice(
        safePage * rowsPerPage,
        safePage * rowsPerPage + rowsPerPage,
    );

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error && !stats) {
        return <Alert severity="error">{error}</Alert>;
    }

    const total = Number(stats?.total) || 0;

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {formatNumber(total)} users match the current filters. Web is the notification option in Settings
                (default On). Jigsaw, Word Wheel, and Sliding Puzzle On means a registered device that has not opted out.
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {apps.map((app) => (
                    <Grid key={app.key} size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card
                            variant="outlined"
                            onClick={() => setAppFilter((current) => (current === app.key ? "" : app.key))}
                            sx={{
                                cursor: "pointer",
                                borderColor: appFilter === app.key ? "primary.main" : undefined,
                                bgcolor: appFilter === app.key ? "action.selected" : undefined,
                            }}
                        >
                            <CardContent>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                    {app.label}
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    <Chip
                                        label={`On ${formatNumber(app.on)}`}
                                        color="success"
                                        size="small"
                                    />
                                    <Chip
                                        label={`Off ${formatNumber(app.off)}`}
                                        variant="outlined"
                                        size="small"
                                    />
                                    {app.key !== PAY_PLAN_APP.WEB ? (
                                        <Chip
                                            label={`None ${formatNumber(app.none)}`}
                                            variant="outlined"
                                            color="default"
                                            size="small"
                                        />
                                    ) : null}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "primary.main" }}>
                        Notification on / off by app
                    </Typography>
                    <BarChart
                        height={320}
                        xAxis={[{ scaleType: "band", data: apps.map((row) => row.label), label: "App" }]}
                        yAxis={[{ label: "Users" }]}
                        series={[
                            { data: apps.map((row) => row.on), label: "On", color: "#2e7d32", stack: "notify" },
                            { data: apps.map((row) => row.off), label: "Off", color: "#9e9e9e", stack: "notify" },
                        ]}
                    />
                </CardContent>
            </Card>
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "primary.main" }}>
                        Who enabled notifications on AWS
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        People with a device token registered on AWS SNS. Notification On is the in-app toggle
                        (default On). Click a name to open the account.
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
                        <Chip
                            label={`All ${formatNumber(awsUsers.length)}`}
                            size="small"
                            color={appFilter === "" ? "primary" : "default"}
                            variant={appFilter === "" ? "filled" : "outlined"}
                            onClick={() => setAppFilter("")}
                        />
                        {APP_ORDER.filter((app) => app.key !== PAY_PLAN_APP.WEB).map((app) => {
                            const count = awsUsers.filter((row) => row.appCode === app.key).length;
                            return (
                                <Chip
                                    key={app.key}
                                    label={`${app.label} ${formatNumber(count)}`}
                                    size="small"
                                    color={appFilter === app.key ? "primary" : "default"}
                                    variant={appFilter === app.key ? "filled" : "outlined"}
                                    onClick={() => setAppFilter((current) => (current === app.key ? "" : app.key))}
                                />
                            );
                        })}
                        <Chip
                            label={`On ${formatNumber(awsOnCount)}`}
                            size="small"
                            color="success"
                        />
                    </Box>
                    {awsError ? <Alert severity="error" sx={{ mb: 2 }}>{awsError}</Alert> : null}
                    {filteredAwsUsers.length === 0 && !awsError ? (
                        <Typography variant="body2" color="text.secondary">
                            No users with an AWS SNS endpoint match the current filters.
                        </Typography>
                    ) : null}
                    {filteredAwsUsers.length > 0 ? (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>App</TableCell>
                                        <TableCell>Platform</TableCell>
                                        <TableCell>Notification</TableCell>
                                        <TableCell>AWS SNS</TableCell>
                                        <TableCell>Updated</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pagedAwsUsers.map((row) => {
                                        const id = row.id;
                                        const openUser = () => {
                                            if (id) navigate(`/admin/account/subscription/${id}`);
                                        };
                                        return (
                                            <TableRow key={`${id}-${row.appCode}-${row.platform}`} hover>
                                                <TableCell>
                                                    {id ? (
                                                        <Link
                                                            component="button"
                                                            type="button"
                                                            onClick={openUser}
                                                            underline="hover"
                                                            sx={{ textAlign: "left" }}
                                                        >
                                                            {displayName(row)}
                                                        </Link>
                                                    ) : (
                                                        displayName(row)
                                                    )}
                                                </TableCell>
                                                <TableCell>{row.email || "—"}</TableCell>
                                                <TableCell>
                                                    {APP_LABEL_BY_KEY[row.appCode] || row.appCode || "—"}
                                                </TableCell>
                                                <TableCell>{platformLabel(row.platform)}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={row.optedIn === false ? "Off" : "On"}
                                                        color={row.optedIn === false ? "default" : "success"}
                                                        variant={row.optedIn === false ? "outlined" : "filled"}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip size="small" label="Yes" color="primary" />
                                                </TableCell>
                                                <TableCell>{formatWhen(row.updatedAt)}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : null}
                    {filteredAwsUsers.length > 0 ? (
                        <TablePagination
                            component="div"
                            count={filteredAwsUsers.length}
                            page={safePage}
                            onPageChange={(_, nextPage) => setPage(nextPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(event) => {
                                const nextSize = Number.parseInt(event.target.value, 10);
                                setRowsPerPage(Number.isFinite(nextSize) ? nextSize : 10);
                                setPage(0);
                            }}
                            rowsPerPageOptions={[10, 25, 50]}
                        />
                    ) : null}
                </CardContent>
            </Card>
        </Box>
    );
}
